
import React, { useEffect, useState } from "react";
import { getEffort } from "../../../services/analytics/analyticsService";
import { EffortResponse } from "../../../interfaces/analytics";
import AnalyticsFilters from "./AnalyticsFilters";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Pagination from "../utility/Pagination";


interface EffortAnalyticsProps {
  authToken: string;
}

const EffortAnalytics: React.FC<EffortAnalyticsProps> = ({ authToken }) => {
  const [data, setData] = useState<EffortResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    teamId: "",
    taskSubTypeId: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const apiParams = {
          ...filters,
          taskSubTypeId: filters.taskSubTypeId
            ? [parseInt(filters.taskSubTypeId, 10)]
            : undefined,
        };
        const response = await getEffort(authToken, apiParams);
        setData(response);
        setCurrentPage(1); // Reset to first page on new data
      } catch (err) {
        setError("Failed to fetch Effort data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authToken, filters]);

  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data ? data.byActionSubType.slice(indexOfFirstItem, indexOfLastItem) : [];
  const totalPages = data ? Math.ceil(data.byActionSubType.length / itemsPerPage) : 0;

  if (loading) {
    return <div className="text-center p-4">Loading Effort data...</div>;
  }

  if (error) {
    return <div className="text-center p-4 text-red-500">{error}</div>;
  }

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Effort Analytics</h2>
      <AnalyticsFilters filters={filters} onFiltersChange={handleFiltersChange} />

      {data ? (
        <>
          <div className="mb-4">
            <h3 className="font-bold">Summary</h3>
            <p>Touches Per Task: {data.summary.touchesPerTask.toFixed(1)}</p>
            <p>First Action Latency (Median): {data.summary.firstActionLatencyMinutesMedian.toFixed(1)} minutes</p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.byActionSubType}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="totalMinutes" fill="#8884d8" name="Total Minutes" />
            </BarChart>
          </ResponsiveContainer>
          <div className="overflow-x-auto mt-4">
            <table className="min-w-full bg-white border">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b">Action SubType</th>
                  <th className="py-2 px-4 border-b">Actions</th>
                  <th className="py-2 px-4 border-b">Total Minutes</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((row, index) => (
                  <tr key={index}>
                    <td className="py-2 px-4 border-b">{row.name}</td>
                    <td className="py-2 px-4 border-b">{row.actions}</td>
                    <td className="py-2 px-4 border-b">{row.totalMinutes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <div className="text-center p-4">No Effort data available for the selected filters.</div>
      )}
    </div>
  );
};

export default EffortAnalytics;
